import { useMutation } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { WeekStartsOn, WritableAccessMetadata } from '@opallabs/concepts'
import { OpalApiRequestBody, OpalApiResponseDocument } from '../../../schema'
import { views } from '../../../v3/views'

export type UpdateViewMutationParams = {
  access?: WritableAccessMetadata
  categoryType?: { id: string; name: string } | null
  endDate?: Date
  id: string
  startDate?: Date
  startMonthIndex?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
  startWeekdayIndex?: WeekStartsOn
  timescale?: 'day' | 'week' | 'month' | 'quarter'
  title?: string
}

type ViewResponseDocument = OpalApiResponseDocument<'UpdateViewV3', 200>
type ViewRequestBody = OpalApiRequestBody<'UpdateViewV3'>

type QueryParams = {
  onMutate?: (params: UpdateViewMutationParams) => void
  onSuccess?: (
    data: ViewResponseDocument,
    params: UpdateViewMutationParams
  ) => void
}

const buildRequestBody = ({
  id,
  categoryType,
  startDate,
  endDate,
  title,
  timescale,
  startMonthIndex,
  startWeekdayIndex,
  access,
}: UpdateViewMutationParams): ViewRequestBody => ({
  data: {
    id: id,
    type: 'view',
    attributes: {
      ...(title && { title }),
      ...(timescale && { default_timescale: timescale }),
      start_at: startDate
        ? formatISO(startDate, { representation: 'date' })
        : undefined,
      end_at: endDate
        ? formatISO(endDate, { representation: 'date' })
        : undefined,
      start_month_index:
        startMonthIndex !== undefined ? startMonthIndex : undefined,
      start_weekday_index:
        startWeekdayIndex !== undefined ? startWeekdayIndex : undefined,
    },
    ...(access !== undefined && { meta: { access } }),
    relationships: {
      category_type:
        categoryType !== undefined
          ? {
              data: categoryType
                ? { id: categoryType.id, type: 'category_type' }
                : null,
            }
          : undefined,
    },
  },
})

export const useUpdateView = ({ onMutate, onSuccess }: QueryParams) => {
  return useMutation({
    mutationFn: (params: UpdateViewMutationParams) => {
      return views.update({
        params: { path: { view_id: params.id }, query: {} },
        requestBody: buildRequestBody(params),
      })
    },
    onMutate,
    onSuccess,
  })
}
